
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import KnowledgeGraphChart from '../../../components/feature/KnowledgeGraphChart';

export default function KnowledgeMapPreview() {
  const [activeLayer, setActiveLayer] = useState('foundation');
  const navigate = useNavigate();

  const knowledgeLayers = {
    foundation: {
      title: '核心基础层',
      description: '财税专业的基础理论知识',
      nodes: [
        { id: 'accounting-basic', name: '会计基础', category: 0, value: 4, description: '会计学基本原理与方法' },
        { id: 'tax-theory', name: '税法理论', category: 1, value: 3, description: '税收法律法规基础' },
        { id: 'finance-theory', name: '财务管理理论', category: 2, value: 3, description: '企业财务管理基本理论' }
      ],
      links: [
        { source: 'accounting-basic', target: 'tax-theory' },
        { source: 'accounting-basic', target: 'finance-theory' }
      ],
      categories: [
        { name: '会计基础', color: '#3b82f6' },
        { name: '税法理论', color: '#10b981' },
        { name: '财务管理', color: '#8b5cf6' }
      ]
    },
    professional: {
      title: '专业核心层',
      description: '各专业领域的核心课程',
      nodes: [
        { id: 'tax-accounting', name: '税务会计', category: 0, value: 3, description: '税务相关会计处理' },
        { id: 'management-accounting', name: '管理会计', category: 0, value: 3, description: '企业内部管理会计' },
        { id: 'tax-planning', name: '纳税筹划', category: 1, value: 3, description: '合法税务筹划' },
        { id: 'internal-control', name: '内部控制', category: 2, value: 3, description: '企业风险控制' },
        { id: 'financial-audit', name: '财务审计', category: 2, value: 3, description: '财务审计实务' }
      ],
      links: [
        { source: 'tax-accounting', target: 'management-accounting' },
        { source: 'tax-accounting', target: 'tax-planning' },
        { source: 'management-accounting', target: 'internal-control' },
        { source: 'internal-control', target: 'financial-audit' }
      ],
      categories: [
        { name: '会计应用', color: '#3b82f6' },
        { name: '税务实务', color: '#10b981' },
        { name: '财务管理', color: '#8b5cf6' }
      ]
    },
    application: {
      title: '技能应用层',
      description: '实践技能与工具应用',
      nodes: [
        { id: 'excel-finance', name: 'Excel应用', category: 0, value: 2, description: '财务数据处理工具' },
        { id: 'big-data-analysis', name: '大数据分析', category: 0, value: 4, description: '大数据技术应用' },
        { id: 'tax-filing-training', name: '纳税申报实训', category: 1, value: 3, description: '实际申报操作' },
        { id: 'tax-admin-training', name: '税收征管实训', category: 1, value: 3, description: '征管流程实践' },
        { id: 'tax-robot', name: '财税机器人', category: 2, value: 4, description: '自动化工具开发' }
      ],
      links: [
        { source: 'excel-finance', target: 'big-data-analysis' },
        { source: 'big-data-analysis', target: 'tax-robot' },
        { source: 'tax-filing-training', target: 'tax-admin-training' },
        { source: 'tax-admin-training', target: 'tax-robot' }
      ],
      categories: [
        { name: '数据技能', color: '#0d9488' },
        { name: '税务实训', color: '#059669' },
        { name: '智能技术', color: '#ea580c' }
      ]
    },
    integration: {
      title: '综合实践层',
      description: '综合应用与创新实践',
      nodes: [
        { id: 'smart-finance-tax', name: '智能财税', category: 0, value: 5, description: 'AI技术在财税中的应用' },
        { id: 'business-finance-system', name: '业财管理系统', category: 0, value: 3, description: '企业信息系统' },
        { id: 'smart-tax-filing', name: '智慧化申报', category: 1, value: 4, description: '智能申报系统' },
        { id: 'labor-skills', name: '劳动技能', category: 2, value: 2, description: '实践技能培养' }
      ],
      links: [
        { source: 'smart-finance-tax', target: 'business-finance-system' },
        { source: 'smart-finance-tax', target: 'smart-tax-filing' },
        { source: 'business-finance-system', target: 'labor-skills' }
      ],
      categories: [
        { name: '智能应用', color: '#ea580c' },
        { name: '税务管理', color: '#059669' },
        { name: '劳动教育', color: '#dc2626' }
      ]
    }
  };

  const currentLayer = knowledgeLayers[activeLayer as keyof typeof knowledgeLayers];

  const handleNodeClick = (node: any) => {
    console.log('预览模式点击节点:', node);
    // 可以在这里添加预览模式下的节点点击处理逻辑
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            知识图谱可视化
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            四层知识体系结构，清晰展示财税大数据专业的知识关联与学习路径
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Layer Selection */}
          <div className="lg:col-span-1">
            <Card>
              <h3 className="text-xl font-bold text-gray-900 mb-4">知识层级</h3>
              <div className="space-y-3">
                {Object.entries(knowledgeLayers).map(([key, layer]) => (
                  <button
                    key={key}
                    onClick={() => setActiveLayer(key)}
                    className={`w-full text-left p-4 rounded-lg transition-all ${
                      activeLayer === key
                        ? 'bg-blue-100 border-2 border-blue-500 text-blue-700'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="font-semibold">{layer.title}</div>
                    <div className="text-sm mt-1 opacity-75">{layer.description}</div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Knowledge Map Visualization */}
          <div className="lg:col-span-2">
            <Card>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">{currentLayer.title}</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/knowledge-map')}
                >
                  <i className="ri-fullscreen-line mr-2"></i>
                  查看完整图谱
                </Button>
              </div>
              
              <KnowledgeGraphChart
                nodes={currentLayer.nodes}
                links={currentLayer.links}
                categories={currentLayer.categories}
                onNodeClick={handleNodeClick}
                height={400}
              />

              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="text-sm text-blue-700">
                  <strong>提示：</strong>可以拖拽节点调整位置，滚轮缩放图谱，点击节点查看详细信息。
                  点击右上角按钮查看完整的交互式知识图谱。
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
